import { useState, useEffect } from 'react';
import { usePermissions } from '@/contexts/PermissionContext';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, GripVertical, Plus, X, Loader2, ChevronDown, ChevronRight, RotateCcw, Settings, Shield } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { MultiSelect } from '@/components/ui/multi-select';
import { Badge } from '@/components/ui/badge';
import { RolePermissionsSelect } from '@/components/ui/role-permissions-select';
import { ConditionalLogicBuilder, ChecklistField, LogicJoin, LogicRule, LogicGroup } from '@/components/ui/conditional-logic-builder';
import { ChecklistAccessSelect, RoleOption, UserOption } from '@/components/ui/checklist-access-select';
import { ReferenceFieldBuilder } from './ReferenceFieldBuilder';
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Define types based on database schema
interface IntermediateTableConfig {
  table: string;
  intermediate_column: string;
  target_column: string;
}

interface RefFilterGroup {
  col_name: string;
  operator: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'LIKE' | 'ILIKE' | 'IN' | 'IS NULL' | 'IS NOT NULL';
  value: string | number | boolean | null;
}

interface RefConfig {
  ref_table: string;
  ref_col: string;
  ref_lookup_col?: string;
  ref_filter_group?: RefFilterGroup;
  intermediate_tables?: IntermediateTableConfig[];
  field_type?: 'TEXT' | 'NUMBER' | 'DATE' | 'CHECKBOX' | 'RADIOBUTTON' | 'USER_ASSIGNMENT' | 'FILE_UPLOAD' | 'MULTI_SELECT';
  is_editable?: boolean;
}

// Template item interface
interface TemplateItem {
  id: string;
  name: string;
  description?: string;
  item_type: 'CHECKBOX' | 'TEXT_INPUT' | 'RADIOBUTTON' | 'MULTISELECT' | 'DATE_INPUT' | 'DATETIME_INPUT' | 'DATE_RANGE' | 'FILE_UPLOAD' | 'NUMBER' | 'REFERENCE_FIELD' | 'URL';
  is_required: boolean;
  category: string;
  display_order: number;
  conditional_logic?: any | null;
  permissions?: string[] | null;
  user_permissions?: string[] | null;
  item_values?: string[] | string | RefConfig[] | null;
  group_id?: string | null;
}

// Template group interface
interface TemplateGroup {
  id: string;
  name: string;
  display_order: number;
  permissions?: string[] | null;
  user_permissions?: string[] | null;
  conditional_logic?: any | null;
}

// Template configuration interface
interface TemplateConfiguration {
  items: TemplateItem[];
  groups: TemplateGroup[];
  default_settings: {
    auto_notify_pm_at_percent: number;
    auto_move_stage_when_complete: boolean;
    estimated_completion_time_hours: number;
  };
}

interface TemplateItemManagerProps {
  templateId: string;
  templateName: string;
  onCancel?: () => void;
}

// Utility function to check if a string is a valid URL
const isValidUrl = (str: string) => {
  try {
    const url = new URL(str)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

// Utility function to check if an item has conditional logic
const hasConditionalLogic = (item: TemplateItem): boolean => {
  return item.conditional_logic && 
         Array.isArray(item.conditional_logic) && 
         item.conditional_logic.length > 0 &&
         item.conditional_logic.some((group: any) => 
           group.rules && 
           Array.isArray(group.rules) && 
           group.rules.length > 0
         );
};

// Utility function to check if a group has conditional logic
const hasGroupConditionalLogic = (group: TemplateGroup): boolean => {
  return group.conditional_logic && 
         Array.isArray(group.conditional_logic) && 
         group.conditional_logic.length > 0 &&
         group.conditional_logic.some((logicGroup: any) => 
           logicGroup.rules && 
           Array.isArray(logicGroup.rules) && 
           logicGroup.rules.length > 0
         );
};

type Role = {
  id: string;
  name: string;
};

export const TemplateItemManager = ({ templateId, templateName, onCancel }: TemplateItemManagerProps) => {
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  
  // Check if user has admin access
  const hasAdminAccess = hasPermission('settings.admin') || hasPermission('manage_roles') || hasPermission('manage_users');
  const canManageTheme = hasPermission('manage_checklists') || hasPermission('manage_settings');
  
  // State for template data
  const [template, setTemplate] = useState<any>(null);
  const [configuration, setConfiguration] = useState<TemplateConfiguration>({
    items: [],
    groups: [],
    default_settings: {
      auto_notify_pm_at_percent: 80,
      auto_move_stage_when_complete: false,
      estimated_completion_time_hours: 2
    }
  });
  
  // State for items and groups
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [groups, setGroups] = useState<TemplateGroup[]>([]);
  const [ungroupedItems, setUngroupedItems] = useState<TemplateItem[]>([]);
  
  // Group expansion state
  const [expandedGroups, setExpandedGroups] = useState<{ [id: string]: boolean }>({});
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [isOptionsDialogOpen, setIsOptionsDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<TemplateItem | null>(null);
  const [currentGroup, setCurrentGroup] = useState<TemplateGroup | null>(null);
  
  // Form state
  const [newItem, setNewItem] = useState<Partial<TemplateItem>>({
    name: '',
    description: '',
    item_type: 'CHECKBOX',
    is_required: false,
    category: 'project',
    group_id: null
  });
  
  const [newGroup, setNewGroup] = useState<Partial<TemplateGroup>>({
    name: '',
    display_order: 0
  });
  
  // Advanced features state
  const [showAdvancedItemFeatures, setShowAdvancedItemFeatures] = useState(false);
  const [showAdvancedGroupFeatures, setShowAdvancedGroupFeatures] = useState(false);
  const [itemLogic, setItemLogic] = useState<LogicGroup[]>([]);
  const [groupLogic, setGroupLogic] = useState<LogicGroup[]>([]);
  const [itemPermissions, setItemPermissions] = useState<string[]>([]);
  const [itemUserPermissions, setItemUserPermissions] = useState<string[]>([]);
  const [groupPermissions, setGroupPermissions] = useState<string[]>([]);
  const [groupUserPermissions, setGroupUserPermissions] = useState<string[]>([]);
  const [itemOptions, setItemOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  
  // Reference field state
  const [refConfig, setRefConfig] = useState<RefConfig>({
    ref_table: '',
    ref_col: '',
    ref_lookup_col: 'id',
    intermediate_tables: [],
    field_type: 'TEXT',
    is_editable: true
  });
  const [showFilterConfig, setShowFilterConfig] = useState(false);
  const [filterGroup, setFilterGroup] = useState<RefFilterGroup>({
    col_name: '',
    operator: '=',
    value: ''
  });
  const [intermediateTable, setIntermediateTable] = useState({
    table: '',
    intermediate_column: '',
    target_column: ''
  });
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState<TemplateItem | null>(null);
  const [reorderLoading, setReorderLoading] = useState(false);
  
  // Other state
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDeletedItems, setShowDeletedItems] = useState(false);
  const [deletedItems, setDeletedItems] = useState<TemplateItem[]>([]);
  const [loadingDeletedItems, setLoadingDeletedItems] = useState(false);
  const [isReferenceFieldBuilderOpen, setIsReferenceFieldBuilderOpen] = useState(false);

  // Create fields for conditional logic from template items
  const checklistFields: ChecklistField[] = [
    // Project-level fields (using negative IDs to avoid conflicts)
    { id: '-1', name: 'project_status', type: 'SELECT' },
    { id: '-2', name: 'installer_partner', type: 'SELECT' },
    { id: '-3', name: 'stage', type: 'SELECT' },
    { id: '-4', name: 'created_at', type: 'DATE' },
    { id: '-5', name: 'updated_at', type: 'DATE' },
    // All template items with their options
    ...ungroupedItems.map(item => ({
      id: item.id,
      name: `[${templateName}] ${item.name}`,
      type: item.item_type,
      options: (item.item_type === 'RADIOBUTTON' || item.item_type === 'MULTISELECT') && Array.isArray(item.item_values)
        ? (item.item_values as string[]).map((val: string) => ({ value: val, label: val }))
        : undefined,
    })),
    ...items.map(item => ({
      id: item.id,
      name: `[${templateName}] ${item.name}`,
      type: item.item_type,
      options: (item.item_type === 'RADIOBUTTON' || item.item_type === 'MULTISELECT') && Array.isArray(item.item_values)
        ? (item.item_values as string[]).map((val: string) => ({ value: val, label: val }))
        : undefined,
    }))
  ];

  // Create role options for access control
  const roleOptions: RoleOption[] = roles.map(role => ({
    label: role.name,
    value: role.name,
    description: `Allow users with ${role.name} role to access this item`
  }));

  // Create user options for access control
  const userOptions: UserOption[] = users.map(user => ({
    label: user.email || user.first_name || user.id,
    value: user.id,
    email: user.email,
    role: user.role
  }));

  // Load template data on component mount
  useEffect(() => {
    fetchTemplateData();
    fetchRoles();
    fetchUsers();
  }, [templateId]);

  const fetchTemplateData = async () => {
    try {
      setLoading(true);
      
      // Fetch template from library
      const { data: templateData, error: templateError } = await supabase
        .from('checklist_templates_library')
        .select('*')
        .eq('id', templateId)
        .single();

      if (templateError) {
        console.error('Error fetching template:', templateError);
        toast({
          title: "Error",
          description: "Failed to load template data.",
          variant: "destructive",
        });
        return;
      }

      setTemplate(templateData);
      
      // Parse configuration
      const config = templateData.configuration || {
        items: [],
        groups: [],
        default_settings: {
          auto_notify_pm_at_percent: 80,
          auto_move_stage_when_complete: false,
          estimated_completion_time_hours: 2
        }
      };
      
      setConfiguration(config);
      setItems(config.items || []);
      setGroups(config.groups || []);
      
      // Separate grouped and ungrouped items
      const grouped = config.items?.filter((item: TemplateItem) => item.group_id) || [];
      const ungrouped = config.items?.filter((item: TemplateItem) => !item.group_id) || [];
      setUngroupedItems(ungrouped);
      
      // Initialize expanded groups state - expand all groups by default
      const expanded: { [id: string]: boolean } = {};
      (config.groups || []).forEach((group: any) => { 
        expanded[group.id] = true; 
      });
      setExpandedGroups(expanded);
      
    } catch (error) {
      console.error('Error fetching template data:', error);
      toast({
        title: "Error",
        description: "Failed to load template data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Group management functions
  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const resetAdvancedFeatures = () => {
    setItemLogic([]);
    setItemPermissions([]);
    setItemUserPermissions([]);
    setShowAdvancedItemFeatures(false);
  };

  const resetGroupAdvancedFeatures = () => {
    setGroupLogic([]);
    setGroupPermissions([]);
    setGroupUserPermissions([]);
    setShowAdvancedGroupFeatures(false);
  };

  const openAddGroupDialog = () => {
    setCurrentGroup(null);
    setNewGroup({ name: '', display_order: 0 });
    resetGroupAdvancedFeatures();
    setIsGroupDialogOpen(true);
  };

  const openAddItemWithGroup = (groupId: string) => {
    setNewItem({ ...newItem, group_id: groupId });
    resetAdvancedFeatures();
    setIsAddDialogOpen(true);
  };

  const openAddItemDialog = () => {
    resetAdvancedFeatures();
    setIsAddDialogOpen(true);
  };

  const openEditGroupDialog = (group: TemplateGroup) => {
    setCurrentGroup(group);
    setNewGroup({ name: group.name, display_order: group.display_order });
    setGroupLogic(group.conditional_logic || []);
    setGroupPermissions(group.permissions || []);
    setGroupUserPermissions(group.user_permissions || []);
    setShowAdvancedGroupFeatures(
      (group.conditional_logic && group.conditional_logic.length > 0) ||
      (group.permissions && group.permissions.length > 0) ||
      (group.user_permissions && group.user_permissions.length > 0)
    );
    setIsGroupDialogOpen(true);
  };

  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case 'CHECKBOX': return 'Checkbox';
      case 'TEXT_INPUT': return 'Text Input';
      case 'RADIOBUTTON': return 'Radio Button';
      case 'MULTISELECT': return 'Multi-Select';
      case 'DATE_INPUT': return 'Date Input';
      case 'DATETIME_INPUT': return 'Date & Time';
      case 'DATE_RANGE': return 'Date Range';
      case 'FILE_UPLOAD': return 'File Upload';
      case 'NUMBER': return 'Number Input';
      case 'REFERENCE_FIELD': return 'Reference Field';
      case 'URL': return 'URL';
      default: return type;
    }
  };

  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const { data, error } = await supabase
        .from('roles')
        .select('id, name')
        .order('name');
      
      if (error) {
        console.error('Error fetching roles:', error);
        return;
      }
      
      setRoles(data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('id, email, first_name, last_name')
        .order('first_name, last_name');
      
      if (error) {
        console.error('Error fetching users:', error);
        return;
      }
      
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const saveTemplate = async () => {
    try {
      setSaving(true);
      
      // Update configuration with current items and groups
      const updatedConfiguration = {
        ...configuration,
        items: [...ungroupedItems, ...items.filter(item => item.group_id)],
        groups: groups
      };
      
      // Update template in library
      const { error } = await supabase
        .from('checklist_templates_library')
        .update({
          configuration: updatedConfiguration,
          total_items_count: updatedConfiguration.items.length,
          updated_at: new Date().toISOString()
        })
        .eq('id', templateId);

      if (error) {
        console.error('Error saving template:', error);
        toast({
          title: "Error",
          description: "Failed to save template.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Template saved successfully.",
      });

      setHasUnsavedChanges(false);
      setConfiguration(updatedConfiguration);
      
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: "Error",
        description: "Failed to save template.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    try {
      if (!newItem.name?.trim()) {
        toast({
          title: "Error",
          description: "Item name is required.",
          variant: "destructive",
        });
        return;
      }

      const newItemData: TemplateItem = {
        id: crypto.randomUUID(),
        name: newItem.name,
        description: newItem.description || '',
        item_type: newItem.item_type || 'CHECKBOX',
        is_required: newItem.is_required || false,
        category: newItem.category || 'project',
        display_order: ungroupedItems.length + 1,
        conditional_logic: itemLogic.length > 0 ? itemLogic : null,
        permissions: itemPermissions.length > 0 ? itemPermissions : null,
        user_permissions: itemUserPermissions.length > 0 ? itemUserPermissions : null,
        item_values: itemOptions.length > 0 ? itemOptions : null,
        group_id: newItem.group_id || null
      };

      const updatedUngroupedItems = [...ungroupedItems, newItemData];
      setUngroupedItems(updatedUngroupedItems);
      setHasUnsavedChanges(true);

      // Reset form
      setNewItem({
        name: '',
        description: '',
        item_type: 'CHECKBOX',
        is_required: false,
        category: 'project',
        group_id: null
      });
      setItemLogic([]);
      setItemPermissions([]);
      setItemUserPermissions([]);
      setItemOptions([]);
      setIsAddDialogOpen(false);

      toast({
        title: "Success",
        description: "Item added successfully.",
      });

    } catch (error) {
      console.error('Error adding item:', error);
      toast({
        title: "Error",
        description: "Failed to add item.",
        variant: "destructive",
      });
    }
  };

  const handleEditItem = async () => {
    if (!currentItem) return;

    try {
      const updatedItem: TemplateItem = {
        ...currentItem,
        name: newItem.name || currentItem.name,
        description: newItem.description || currentItem.description || '',
        item_type: newItem.item_type || currentItem.item_type,
        is_required: newItem.is_required ?? currentItem.is_required,
        category: newItem.category || currentItem.category,
        conditional_logic: itemLogic.length > 0 ? itemLogic : null,
        permissions: itemPermissions.length > 0 ? itemPermissions : null,
        user_permissions: itemUserPermissions.length > 0 ? itemUserPermissions : null,
        item_values: itemOptions.length > 0 ? itemOptions : currentItem.item_values
      };

      // Update in appropriate array
      if (currentItem.group_id) {
        const updatedItems = items.map(item => 
          item.id === currentItem.id ? updatedItem : item
        );
        setItems(updatedItems);
      } else {
        const updatedUngroupedItems = ungroupedItems.map(item => 
          item.id === currentItem.id ? updatedItem : item
        );
        setUngroupedItems(updatedUngroupedItems);
      }

      setHasUnsavedChanges(true);
      setIsEditDialogOpen(false);
      setCurrentItem(null);

      toast({
        title: "Success",
        description: "Item updated successfully.",
      });

    } catch (error) {
      console.error('Error updating item:', error);
      toast({
        title: "Error",
        description: "Failed to update item.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      // Remove from appropriate array
      if (items.find(item => item.id === itemId)) {
        setItems(items.filter(item => item.id !== itemId));
      } else {
        setUngroupedItems(ungroupedItems.filter(item => item.id !== itemId));
      }

      setHasUnsavedChanges(true);

      toast({
        title: "Success",
        description: "Item deleted successfully.",
      });

    } catch (error) {
      console.error('Error deleting item:', error);
      toast({
        title: "Error",
        description: "Failed to delete item.",
        variant: "destructive",
      });
    }
  };

  const handleAddGroup = async () => {
    try {
      if (!newGroup.name?.trim()) {
        toast({
          title: "Error",
          description: "Group name is required.",
          variant: "destructive",
        });
        return;
      }

      const newGroupData: TemplateGroup = {
        id: crypto.randomUUID(),
        name: newGroup.name,
        display_order: groups.length + 1,
        conditional_logic: groupLogic.length > 0 ? groupLogic : null,
        permissions: groupPermissions.length > 0 ? groupPermissions : null,
        user_permissions: groupUserPermissions.length > 0 ? groupUserPermissions : null
      };

      setGroups([...groups, newGroupData]);
      setHasUnsavedChanges(true);

      // Reset form
      setNewGroup({
        name: '',
        display_order: 0
      });
      setGroupLogic([]);
      setGroupPermissions([]);
      setGroupUserPermissions([]);
      setIsGroupDialogOpen(false);

      toast({
        title: "Success",
        description: "Group added successfully.",
      });

    } catch (error) {
      console.error('Error adding group:', error);
      toast({
        title: "Error",
        description: "Failed to add group.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      // Move items from group to ungrouped
      const groupItems = items.filter(item => item.group_id === groupId);
      const updatedUngroupedItems = [...ungroupedItems, ...groupItems.map(item => ({ ...item, group_id: null }))];
      
      setUngroupedItems(updatedUngroupedItems);
      setItems(items.filter(item => item.group_id !== groupId));
      setGroups(groups.filter(group => group.id !== groupId));
      setHasUnsavedChanges(true);

      toast({
        title: "Success",
        description: "Group deleted successfully.",
      });

    } catch (error) {
      console.error('Error deleting group:', error);
      toast({
        title: "Error",
        description: "Failed to delete group.",
        variant: "destructive",
      });
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    try {
      setReorderLoading(true);

      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;
      const { source, destination, type } = result;

      if (type === 'group') {
        // Handle group reordering
        const newGroups = Array.from(groups);
        const [removed] = newGroups.splice(sourceIndex, 1);
        newGroups.splice(destinationIndex, 0, removed);
        
        // Update display_order for all groups
        const updatedGroups = newGroups.map((group, index) => ({
          ...group,
          display_order: index + 1
        }));
        
        setGroups(updatedGroups);
        setHasUnsavedChanges(true);
        return;
      }

      // Handle item reordering
      const sourceGroupId = source.droppableId === 'ungrouped-items' ? null : source.droppableId.replace('group-', '');
      const destGroupId = destination.droppableId === 'ungrouped-items' ? null : destination.droppableId.replace('group-', '');
      
      // Extract item ID from draggableId (remove 'item-' prefix)
      const itemId = result.draggableId.replace('item-', '');
      
      if (source.droppableId === destination.droppableId) {
        // Within same group/ungrouped - reorder items
        if (source.droppableId === 'ungrouped-items') {
          const reorderedItems = Array.from(ungroupedItems);
          const [removed] = reorderedItems.splice(sourceIndex, 1);
          reorderedItems.splice(destinationIndex, 0, removed);

          // Update display_order
          const updatedItems = reorderedItems.map((item, index) => ({
            ...item,
            display_order: index + 1
          }));

          setUngroupedItems(updatedItems);
        } else {
          // Within same group
          const groupItems = items.filter(item => item.group_id === sourceGroupId);
          const reorderedItems = Array.from(groupItems);
          const [removed] = reorderedItems.splice(sourceIndex, 1);
          reorderedItems.splice(destinationIndex, 0, removed);

          // Update display_order
          const updatedItems = reorderedItems.map((item, index) => ({
            ...item,
            display_order: index + 1
          }));

          // Update items array
          const otherItems = items.filter(item => item.group_id !== sourceGroupId);
          setItems([...otherItems, ...updatedItems]);
        }
        setHasUnsavedChanges(true);
      } else {
        // Moving between groups or to/from ungrouped
        let sourceItems: TemplateItem[] = [];
        let destItems: TemplateItem[] = [];
        
        if (source.droppableId === 'ungrouped-items') {
          sourceItems = [...ungroupedItems];
        } else {
          sourceItems = items.filter(item => item.group_id === sourceGroupId);
        }
        
        if (destination.droppableId === 'ungrouped-items') {
          destItems = [...ungroupedItems];
        } else {
          destItems = items.filter(item => item.group_id === destGroupId);
        }

        // Remove from source
        const [movedItem] = sourceItems.splice(sourceIndex, 1);
        movedItem.group_id = destGroupId;
        
        // Add to destination
        destItems.splice(destinationIndex, 0, movedItem);
        
        // Update display_order for destination
        const updatedDestItems = destItems.map((item, index) => ({
          ...item,
          display_order: index + 1
        }));

        // Update state
        if (destination.droppableId === 'ungrouped-items') {
          setUngroupedItems(updatedDestItems);
          if (source.droppableId !== 'ungrouped-items') {
            const otherItems = items.filter(item => item.group_id !== sourceGroupId);
            setItems(otherItems);
          }
        } else {
          const otherItems = items.filter(item => item.group_id !== destGroupId && item.group_id !== sourceGroupId);
          setItems([...otherItems, ...updatedDestItems]);
          if (source.droppableId === 'ungrouped-items') {
            setUngroupedItems(sourceItems);
          }
        }
        setHasUnsavedChanges(true);
      }

    } catch (error) {
      console.error('Error reordering items:', error);
      toast({
        title: "Error",
        description: "Failed to reorder items.",
        variant: "destructive",
      });
    } finally {
      setReorderLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Edit Template: {templateName}</h2>
          <p className="text-muted-foreground">
            Manage checklist items and groups for this template
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={saveTemplate} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Template
          </Button>
        </div>
      </div>

      {/* Template Info */}
      <Card>
        <CardHeader>
          <CardTitle>Template Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Name:</span>
              <p className="text-muted-foreground">{template?.name}</p>
            </div>
            <div>
              <span className="font-medium">Category:</span>
              <p className="text-muted-foreground">{template?.category || 'None'}</p>
            </div>
            <div>
              <span className="font-medium">Pipeline:</span>
              <p className="text-muted-foreground">{template?.pipeline_name || 'None'}</p>
            </div>
            <div>
              <span className="font-medium">Stage:</span>
              <p className="text-muted-foreground">{template?.stage_name || 'None'}</p>
            </div>
            <div>
              <span className="font-medium">Total Items:</span>
              <p className="text-muted-foreground">{configuration.items?.length || 0}</p>
            </div>
            <div>
              <span className="font-medium">Groups:</span>
              <p className="text-muted-foreground">{configuration.groups?.length || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <DragDropContext onDragEnd={handleDragEnd}>
        {/* Ungrouped Items */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Ungrouped Items</CardTitle>
              <Button onClick={openAddItemDialog} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Droppable droppableId="ungrouped-items">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-2"
                >
                  {ungroupedItems.map((item, index) => (
                    <Draggable key={item.id} draggableId={`item-${item.id}`} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div>
                              <h4 className="font-medium text-foreground">{item.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {item.item_type} • {item.category}
                                {item.is_required && ' • Required'}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {hasConditionalLogic(item) && (
                              <Badge variant="outline">Conditional</Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentItem(item);
                                setNewItem({
                                  name: item.name,
                                  description: item.description,
                                  item_type: item.item_type,
                                  is_required: item.is_required,
                                  category: item.category,
                                  group_id: item.group_id
                                });
                                setItemLogic(item.conditional_logic || []);
                                setItemPermissions(item.permissions || []);
                                setItemUserPermissions(item.user_permissions || []);
                                setItemOptions(Array.isArray(item.item_values) ? item.item_values as string[] : []);
                                setShowAdvancedItemFeatures(
                                  (item.conditional_logic && item.conditional_logic.length > 0) ||
                                  (item.permissions && item.permissions.length > 0) ||
                                  (item.user_permissions && item.user_permissions.length > 0)
                                );
                                setIsEditDialogOpen(true);
                              }}
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteItem(item.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
            
            {ungroupedItems.length === 0 && (
              <p className="text-muted-foreground">No items created yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Groups */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Groups</CardTitle>
              <Button onClick={openAddGroupDialog} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Group
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {groups.length === 0 ? (
              <p className="text-muted-foreground">No groups created yet.</p>
            ) : (
              <Droppable droppableId="groups-droppable" type="group">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                    {groups.map((group, groupIdx) => (
                      <Draggable key={group.id} draggableId={`group-${group.id}`} index={groupIdx}>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps}>
                            <div className="border rounded-md">
                              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer" onClick={() => toggleGroup(group.id)} {...provided.dragHandleProps}>
                                <div className="flex items-center gap-2">
                                  {expandedGroups[group.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  <span className="font-semibold text-sm">{group.name}</span>
                                  {hasGroupConditionalLogic(group) && (
                                    <div className="flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-800 rounded-full">
                                      <Shield className="h-3 w-3" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-7 px-2 text-xs hover:bg-gray-200" 
                                    onClick={e => { e.stopPropagation(); openAddItemWithGroup(group.id); }}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={e => { e.stopPropagation(); openEditGroupDialog(group); }}>Edit</Button>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-500" onClick={e => { e.stopPropagation(); handleDeleteGroup(group.id); }}>Delete</Button>
                                </div>
                              </div>
                              {expandedGroups[group.id] && (
                                <Droppable droppableId={`group-${group.id}`} type="item">
                                  {(provided) => (
                                    <div className="divide-y" ref={provided.innerRef} {...provided.droppableProps}>
                                      {items.filter(item => item.group_id === group.id).length > 0 ? 
                                        items.filter(item => item.group_id === group.id).map((item, itemIdx) => (
                                          <Draggable key={item.id} draggableId={`item-${item.id}`} index={itemIdx}>
                                            {(provided) => (
                                              <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="p-3 flex justify-between items-center">
                                                <div>
                                                  <div className="flex items-center gap-2">
                                                    <h4 className="font-medium text-sm text-foreground">{item.name}</h4>
                                                    {item.is_required && (<span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-800 rounded-full">Required</span>)}
                                                    {hasConditionalLogic(item) && (
                                                      <div className="flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-800 rounded-full">
                                                        <Shield className="h-3 w-3" />
                                                      </div>
                                                    )}
                                                  </div>
                                                  <p className="text-xs text-muted-foreground">{getItemTypeLabel(item.item_type)}</p>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                                                    setCurrentItem(item);
                                                    setNewItem({
                                                      name: item.name,
                                                      description: item.description,
                                                      item_type: item.item_type,
                                                      is_required: item.is_required,
                                                      category: item.category,
                                                      group_id: item.group_id
                                                    });
                                                    setItemLogic(item.conditional_logic || []);
                                                    setItemPermissions(item.permissions || []);
                                                    setItemUserPermissions(item.user_permissions || []);
                                                    setItemOptions(Array.isArray(item.item_values) ? item.item_values as string[] : []);
                                                    setShowAdvancedItemFeatures(
                                                      (item.conditional_logic && item.conditional_logic.length > 0) ||
                                                      (item.permissions && item.permissions.length > 0) ||
                                                      (item.user_permissions && item.user_permissions.length > 0)
                                                    );
                                                    setIsEditDialogOpen(true);
                                                  }}>Edit</Button>
                                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteItem(item.id)}><Trash2 size={14} /></Button>
                                                </div>
                                              </div>
                                            )}
                                          </Draggable>
                                        )) : 
                                        <div className="p-3 text-muted-foreground text-sm">No items in this group</div>
                                      }
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </CardContent>
        </Card>
      </DragDropContext>

      {/* Add Item Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} modal={true}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Item</DialogTitle>
            <DialogDescription>
              Create a new checklist item for this template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="item-name">Item Name</Label>
              <Input
                id="item-name"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="Enter item name"
              />
            </div>
            <div>
              <Label htmlFor="item-description">Description (Optional)</Label>
              <Textarea
                id="item-description"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Enter item description"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="item-type">Item Type</Label>
              <Select
                value={newItem.item_type}
                onValueChange={(value: any) => setNewItem({ ...newItem, item_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHECKBOX">Checkbox</SelectItem>
                  <SelectItem value="TEXT_INPUT">Text Input</SelectItem>
                  <SelectItem value="RADIOBUTTON">Radio Button</SelectItem>
                  <SelectItem value="MULTISELECT">Multi Select</SelectItem>
                  <SelectItem value="DATE_INPUT">Date Input</SelectItem>
                  <SelectItem value="DATETIME_INPUT">Date &amp; Time</SelectItem>
                  <SelectItem value="FILE_UPLOAD">File Upload</SelectItem>
                  <SelectItem value="NUMBER">Number</SelectItem>
                  <SelectItem value="URL">URL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="item-category">Category</Label>
              <Select
                value={newItem.category}
                onValueChange={(value) => setNewItem({ ...newItem, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="communication">Communication</SelectItem>
                  <SelectItem value="quality">Quality</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="item-required"
                checked={newItem.is_required}
                onCheckedChange={(checked) => setNewItem({ ...newItem, is_required: !!checked })}
              />
              <Label htmlFor="item-required">Required</Label>
            </div>

            {/* Advanced Features Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="show_advanced_features"
                checked={showAdvancedItemFeatures}
                onCheckedChange={(checked) => {
                  setShowAdvancedItemFeatures(!!checked);
                  if (!checked) {
                    setItemLogic([]);
                    setItemPermissions([]);
                    setItemUserPermissions([]);
                  }
                }}
              />
              <Label htmlFor="show_advanced_features">Enable role permissions and conditional logic</Label>
            </div>

            {/* Advanced Features Section */}
            {showAdvancedItemFeatures && (
              <div className="grid gap-4 p-4 border rounded-md bg-muted/20">
                <h4 className="font-medium text-sm text-foreground">Advanced Features</h4>
                
                <div className="grid gap-2">
                  <Label htmlFor="item_access">Access Control (Who can see this item)</Label>
                  <ChecklistAccessSelect
                    id="item_access"
                    roleOptions={roleOptions}
                    userOptions={userOptions}
                    selectedRoles={itemPermissions}
                    selectedUsers={itemUserPermissions}
                    onRolesChange={setItemPermissions}
                    onUsersChange={setItemUserPermissions}
                    placeholder="Select roles and users that can access this item..."
                    disabled={rolesLoading || usersLoading}
                    maxDisplay={2}
                  />
                  {(rolesLoading || usersLoading) && (
                    <div className="text-xs text-muted-foreground">Loading access options...</div>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="item_conditional_logic">Conditional Logic (Show/require this item based on other fields)</Label>
                  <ConditionalLogicBuilder
                    fields={checklistFields}
                    value={itemLogic}
                    onChange={setItemLogic}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddItem}>
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} modal={true}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
            <DialogDescription>
              Update the item details and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-item-name">Item Name</Label>
              <Input
                id="edit-item-name"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="Enter item name"
              />
            </div>
            <div>
              <Label htmlFor="edit-item-description">Description</Label>
              <Textarea
                id="edit-item-description"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Enter item description"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="edit-item-type">Item Type</Label>
              <Select
                value={newItem.item_type}
                onValueChange={(value: any) => setNewItem({ ...newItem, item_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHECKBOX">Checkbox</SelectItem>
                  <SelectItem value="TEXT_INPUT">Text Input</SelectItem>
                  <SelectItem value="RADIOBUTTON">Radio Button</SelectItem>
                  <SelectItem value="MULTISELECT">Multi Select</SelectItem>
                  <SelectItem value="DATE_INPUT">Date Input</SelectItem>
                  <SelectItem value="DATETIME_INPUT">Date &amp; Time</SelectItem>
                  <SelectItem value="FILE_UPLOAD">File Upload</SelectItem>
                  <SelectItem value="NUMBER">Number</SelectItem>
                  <SelectItem value="URL">URL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-item-category">Category</Label>
              <Select
                value={newItem.category}
                onValueChange={(value) => setNewItem({ ...newItem, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="communication">Communication</SelectItem>
                  <SelectItem value="quality">Quality</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-item-required"
                checked={newItem.is_required}
                onCheckedChange={(checked) => setNewItem({ ...newItem, is_required: !!checked })}
              />
              <Label htmlFor="edit-item-required">Required</Label>
            </div>

            {/* Advanced Features Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit_show_advanced_features"
                checked={showAdvancedItemFeatures}
                onCheckedChange={(checked) => {
                  setShowAdvancedItemFeatures(!!checked);
                  if (!checked) {
                    setItemLogic([]);
                    setItemPermissions([]);
                    setItemUserPermissions([]);
                  }
                }}
              />
              <Label htmlFor="edit_show_advanced_features">Enable role permissions and conditional logic</Label>
            </div>

            {/* Advanced Features Section */}
            {showAdvancedItemFeatures && (
              <div className="grid gap-4 p-4 border rounded-md bg-muted/20">
                <h4 className="font-medium text-sm text-foreground">Advanced Features</h4>
                
                <div className="grid gap-2">
                  <Label htmlFor="edit_item_access">Access Control (Who can see this item)</Label>
                  <ChecklistAccessSelect
                    id="edit_item_access"
                    roleOptions={roleOptions}
                    userOptions={userOptions}
                    selectedRoles={itemPermissions}
                    selectedUsers={itemUserPermissions}
                    onRolesChange={setItemPermissions}
                    onUsersChange={setItemUserPermissions}
                    placeholder="Select roles and users that can access this item..."
                    disabled={rolesLoading || usersLoading}
                    maxDisplay={2}
                  />
                  {(rolesLoading || usersLoading) && (
                    <div className="text-xs text-muted-foreground">Loading access options...</div>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="edit_item_conditional_logic">Conditional Logic (Show/require this item based on other fields)</Label>
                  <ConditionalLogicBuilder
                    fields={checklistFields}
                    value={itemLogic}
                    onChange={setItemLogic}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditItem}>
              Update Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Group Dialog */}
      <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen} modal={true}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentGroup ? 'Edit Group' : 'Add New Group'}</DialogTitle>
            <DialogDescription>
              {currentGroup ? 'Update the group details and settings.' : 'Create a new group to organize checklist items.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="group-name">Group Name</Label>
              <Input
                id="group-name"
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                placeholder="Enter group name"
              />
            </div>

            {/* Advanced Features Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="group_show_advanced_features"
                checked={showAdvancedGroupFeatures}
                onCheckedChange={(checked) => {
                  setShowAdvancedGroupFeatures(!!checked);
                  if (!checked) {
                    setGroupLogic([]);
                    setGroupPermissions([]);
                    setGroupUserPermissions([]);
                  }
                }}
              />
              <Label htmlFor="group_show_advanced_features">Enable role permissions and conditional logic</Label>
            </div>

            {/* Advanced Features Section */}
            {showAdvancedGroupFeatures && (
              <div className="grid gap-4 p-4 border rounded-md bg-muted/20">
                <h4 className="font-medium text-sm text-foreground">Advanced Features</h4>
                
                <div className="grid gap-2">
                  <Label htmlFor="group_access">Access Control (Who can see this group)</Label>
                  <ChecklistAccessSelect
                    id="group_access"
                    roleOptions={roleOptions}
                    userOptions={userOptions}
                    selectedRoles={groupPermissions}
                    selectedUsers={groupUserPermissions}
                    onRolesChange={setGroupPermissions}
                    onUsersChange={setGroupUserPermissions}
                    placeholder="Select roles and users that can access this group..."
                    disabled={rolesLoading || usersLoading}
                    maxDisplay={2}
                  />
                  {(rolesLoading || usersLoading) && (
                    <div className="text-xs text-muted-foreground">Loading access options...</div>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="group_conditional_logic">Conditional Logic (Show/require this group based on other fields)</Label>
                  <ConditionalLogicBuilder
                    fields={checklistFields}
                    value={groupLogic}
                    onChange={setGroupLogic}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGroupDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddGroup}>
              {currentGroup ? 'Update Group' : 'Add Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
